import { AdminUser } from '@app-types/admin';
import { formatCalendarDate, formatDateOnly } from '@utils/dateUtils';

// Assessment ID Formatting Utility
export const formatAssessmentId = (assessmentId: number | string | null | undefined): string => {
  if (assessmentId === null || assessmentId === undefined) {
    return '-'; // No assessment created yet
  }
  const id = typeof assessmentId === 'string' ? Number.parseInt(assessmentId, 10) : assessmentId;
  return `AS-${id.toString().padStart(6, '0')}`;
};

// CSV Export Utilities

export const downloadCSV = (data: Record<string, unknown>[], filename: string) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
};

const convertToCSV = (data: Record<string, unknown>[]): string => {
  if (!data || data.length === 0) return '';

  // Get all unique keys from all objects
  const allKeys = Array.from(new Set(data.flatMap((obj) => Object.keys(obj))));

  // Create header row
  const header = allKeys.map(escapeCSVValue).join(',');

  // Create data rows
  const rows = data.map((obj) => allKeys.map((key) => escapeCSVValue(obj[key] || '')).join(','));

  return [header, ...rows].join('\n');
};

const escapeCSVValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // If value contains comma, newline, or quote, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

// A free-text cell starting with = + - @ (or tab/CR) is run as a formula by spreadsheet
// apps (S10). Applied per free-text column, not in escapeCSVValue, so numeric columns
// such as negative amounts are exported unchanged.
const neutralizeFormula = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

interface SupplierExport {
  name: string;
  vendor_id?: string;
  tier_name: string;
  status: string;
  last_note?: string | null;
  created_at: string;
}

// Maps a Suppliers-table column key to the CSV column builder for that field.
// Keeping this in sync with the table's visible-columns keys ensures export always
// matches what the user currently sees on screen (bug #1071).
const SUPPLIER_EXPORT_COLUMNS: Record<string, { header: string; getValue: (supplier: SupplierExport) => unknown }> = {
  name: { header: 'Supplier Name', getValue: (s) => s.name },
  vendor_id: { header: 'Supplier Number', getValue: (s) => s.vendor_id || 'N/A' },
  tier: { header: 'Tier', getValue: (s) => s.tier_name },
  status: { header: 'Status', getValue: (s) => s.status },
  last_note: { header: 'Last Note', getValue: (s) => s.last_note || '' },
  created_date: { header: 'Created Date', getValue: (s) => formatDateOnly(s.created_at) }
};

interface ContractExport {
  contract_number: string;
  name: string;
  supplier_name?: string;
  supplier_id: number;
  contract_manager_name?: string;
  contract_managers?: Array<{ id: number; name: string }>;
  contract_owner_name?: string;
  contract_owners?: Array<{ id: number; name: string }>;
  amount?: number;
  start_date: string;
  end_date: string;
  status: string;
  reassessment_status?: { status: string; days_until_due: number } | null;
  contract_type?: string;
  division?: string;
  division_name?: string | null;
  cost_center?: string;
  tier?: string;
}

// Maps a Contracts-table column key to the CSV column builder for that field.
// Column keys here match `contractColumns` in ContractsPage.tsx (bug #1071).
const CONTRACT_EXPORT_COLUMNS: Record<string, { header: string; getValue: (contract: ContractExport) => unknown }> = {
  contract_number: { header: 'Contract Number', getValue: (c) => c.contract_number },
  name: { header: 'Name', getValue: (c) => c.name },
  supplier: { header: 'Supplier', getValue: (c) => c.supplier_name || `Supplier #${c.supplier_id}` },
  manager: {
    header: 'Manager',
    getValue: (c) =>
      c.contract_managers && c.contract_managers.length > 0
        ? c.contract_managers.filter(Boolean).map((m) => m.name).join(', ')
        : c.contract_manager_name || 'Unassigned'
  },
  owner: {
    header: 'Contract Owner',
    getValue: (c) =>
      c.contract_owners && c.contract_owners.length > 0
        ? c.contract_owners.filter(Boolean).map((o) => o.name).join(', ')
        : c.contract_owner_name || 'Unassigned'
  },
  contract_type: { header: 'Type', getValue: (c) => c.contract_type || 'N/A' },
  division: { header: 'Division', getValue: (c) => c.division_name || c.division || 'N/A' },
  cost_center: { header: 'Cost Center', getValue: (c) => c.cost_center || 'N/A' },
  tier: { header: 'Tier', getValue: (c) => c.tier || 'N/A' },
  value: { header: 'Value', getValue: (c) => (c.amount ? `$${c.amount.toLocaleString()}` : 'N/A') },
  start_date: { header: 'Start Date', getValue: (c) => formatCalendarDate(c.start_date) },
  end_date: { header: 'End Date', getValue: (c) => formatCalendarDate(c.end_date) },
  status: { header: 'Status', getValue: (c) => c.status },
  reassessment: { header: 'Reassessment', getValue: (c) => c.reassessment_status?.status || 'N/A' }
};

interface AssessmentExport {
  assessment_id: number;
  assessment_name: string;
  supplier_name?: string | null;
  contract_number: string;
  tier_name: string;
  assessment_status: string;
  assessor_name?: string | null;
  assessor_email?: string | null;
  scheduled_date?: string | null;
  completed_date?: string | null;
  high_gaps?: number | null;
  medium_gaps?: number | null;
  low_gaps?: number | null;
  total_gap_count?: number | null;
  created_at?: string | null;
}

// Maps an Assessments-table column key to the CSV column builder for that field.
// Column keys here match `columns` in AssessmentsPage.tsx (bug #1071).
const ASSESSMENT_EXPORT_COLUMNS: Record<string, { header: string; getValue: (a: AssessmentExport) => unknown }> = {
  assessment_id: { header: 'Assessment #', getValue: (a) => formatAssessmentId(a.assessment_id) },
  name: { header: 'Assessment Name', getValue: (a) => a.assessment_name },
  supplier: { header: 'Supplier', getValue: (a) => a.supplier_name },
  contract: { header: 'Contract Number', getValue: (a) => a.contract_number },
  tier: { header: 'Tier', getValue: (a) => a.tier_name },
  status: { header: 'Status', getValue: (a) => a.assessment_status },
  assessor: { header: 'Assessor', getValue: (a) => a.assessor_name || 'Unassigned' },
  created_at: { header: 'Created', getValue: (a) => formatDateOnly(a.created_at, 'N/A') }
};

// Specific export functions for each data type
// `visibleColumnKeys` must match the Suppliers table's currently visible column keys
// (name, vendor_id, tier, status, last_note, created_date) so the export mirrors what
// the user sees on screen instead of a fixed, independent column set (bug #1071).
export const exportSuppliers = (suppliers: SupplierExport[], visibleColumnKeys: string[]) => {
  const columns = visibleColumnKeys
    .map((key) => SUPPLIER_EXPORT_COLUMNS[key])
    .filter((column): column is (typeof SUPPLIER_EXPORT_COLUMNS)[string] => Boolean(column));

  const exportData = suppliers.map((supplier) =>
    columns.reduce<Record<string, unknown>>((row, column) => {
      row[column.header] = column.getValue(supplier);
      return row;
    }, {})
  );

  downloadCSV(exportData, 'suppliers');
};

// `visibleColumnKeys` must match the Contracts table's currently visible column keys
// so the export mirrors what the user sees on screen (bug #1071).
export const exportContracts = (contracts: ContractExport[], visibleColumnKeys: string[]) => {
  const columns = visibleColumnKeys
    .map((key) => CONTRACT_EXPORT_COLUMNS[key])
    .filter((column): column is (typeof CONTRACT_EXPORT_COLUMNS)[string] => Boolean(column));

  const exportData = contracts.map((contract) =>
    columns.reduce<Record<string, unknown>>((row, column) => {
      row[column.header] = column.getValue(contract);
      return row;
    }, {})
  );

  downloadCSV(exportData, 'contracts');
};

// `visibleColumnKeys` must match the Assessments table's currently visible column keys
// so the export mirrors what the user sees on screen (bug #1071).
export const exportAssessments = (assessments: AssessmentExport[], visibleColumnKeys: string[]) => {
  const columns = visibleColumnKeys
    .map((key) => ASSESSMENT_EXPORT_COLUMNS[key])
    .filter((column): column is (typeof ASSESSMENT_EXPORT_COLUMNS)[string] => Boolean(column));

  const exportData = assessments.map((assessment) =>
    columns.reduce<Record<string, unknown>>((row, column) => {
      row[column.header] = column.getValue(assessment);
      return row;
    }, {})
  );

  downloadCSV(exportData, 'assessments');
};

const getAuthSourceLabel = (source: string | null): string => {
  if (source === 'microsoft') return 'Microsoft SSO';
  if (source === 'keycloak') return 'Keycloak SSO';
  if (source === 'local') return 'Local Password';
  return 'None';
};

const getAccountSourceLabel = (source: AdminUser['account_source']): string => {
  const sourceLabels: Record<string, string> = {
    manual: 'Manually Created',
    microsoft_sync: 'Imported from Microsoft',
    keycloak_sync: 'Imported from Keycloak',
    data_import: 'Imported via Data'
  };
  return sourceLabels[source] || source;
};

// Maps a Users-table column key to the CSV column builder for that field.
// Column keys here match `userColumns` in UserManagementPage.tsx (bug #1071) —
// includes division_name/account_source, which the pre-fix export always dropped
// regardless of visibility. `actions` has no export equivalent, so it's omitted.
const USER_EXPORT_COLUMNS: Record<string, { header: string; getValue: (user: AdminUser) => unknown }> = {
  email: { header: 'Email', getValue: (u) => u.email },
  first_name: { header: 'Name', getValue: (u) => `${u.first_name} ${u.last_name}` },
  contact_id: { header: 'ContactID', getValue: (u) => neutralizeFormula(u.contact_id ?? '') },
  user_id: { header: 'UserID', getValue: (u) => neutralizeFormula(u.user_id ?? '') },
  role_names: { header: 'Roles', getValue: (u) => u.role_names?.join(', ') || 'Contact' },
  division_name: { header: 'Division', getValue: (u) => u.division_name || '' },
  auth_source: { header: 'Auth', getValue: (u) => getAuthSourceLabel(u.auth_source) },
  account_source: { header: 'Account Source', getValue: (u) => getAccountSourceLabel(u.account_source) },
  is_active: { header: 'Status', getValue: (u) => (u.is_active ? 'Active' : 'Inactive') },
  can_login: { header: 'Login Access', getValue: (u) => (u.can_login ? 'Enabled' : 'Disabled') }
};

// `visibleColumnKeys` must match the Users table's currently visible column keys so
// the export mirrors what the user sees on screen (bug #1071).
export const exportUsers = (users: AdminUser[], visibleColumnKeys: string[]) => {
  const columns = visibleColumnKeys
    .map((key) => USER_EXPORT_COLUMNS[key])
    .filter((column): column is (typeof USER_EXPORT_COLUMNS)[string] => Boolean(column));

  const exportData = users.map((user) =>
    columns.reduce<Record<string, unknown>>((row, column) => {
      row[column.header] = column.getValue(user);
      return row;
    }, {})
  );

  downloadCSV(exportData, 'users');
};

interface ScheduleItemContractExport {
  contract_number: string;
  name: string;
  tier: string;
  supplier_name: string;
  division: string;
  state: string;
  urgency?: string;
  target_date: string;
  contract_owners: Array<{ name: string }>;
  contract_managers: Array<{ name: string }>;
  assessment?: { id: number; name: string; number: number; status: string } | null;
}

interface ScheduleItemExport {
  id: number | null;
  assessor: { id: number; name: string } | null;
  processed_by: { id: number; name: string } | null;
  meeting: {
    meeting_id: string;
    scheduled_datetime: string;
    status: string;
  } | null;
  contracts: ScheduleItemContractExport[];
}

// One CSV row is produced per (schedule item, contract) pair — a schedule item can
// group several contracts into one meeting, so `getValue` receives both.
type ScheduleItemColumn = {
  header: string;
  getValue: (item: ScheduleItemExport, contract: ScheduleItemContractExport) => unknown;
};

// Maps an Assessment-Meetings-table column key to the CSV column builder for that
// field. Column keys here match `columns` in AssessorMeetingsPage.tsx (bug #1071).
// `evidence_status`/`evidence_due_date` are omitted: the table itself always shows
// "N/A" for them (not available from the scheduler API), so there's nothing to export.
const ASSESSOR_MEETING_EXPORT_COLUMNS: Record<string, ScheduleItemColumn> = {
  contract_number: { header: 'Contract Number', getValue: (_item, c) => c.contract_number },
  meeting_date: { header: 'Meeting Date', getValue: (item) => formatDateOnly(item.meeting?.scheduled_datetime, 'Not scheduled') },
  meeting_status: { header: 'Meeting Status', getValue: (item) => item.meeting?.status || 'N/A' },
  assessment_id: { header: 'Assessment #', getValue: (_item, c) => (c.assessment ? formatAssessmentId(c.assessment.number) : '') },
  assessment_name: { header: 'Assessment Name', getValue: (_item, c) => c.assessment?.name || '' },
  assessment_status: { header: 'Assessment Status', getValue: (_item, c) => c.assessment?.status || 'Not Created' },
  supplier_name: { header: 'Supplier', getValue: (_item, c) => c.supplier_name },
  tier: { header: 'Tier', getValue: (_item, c) => c.tier },
  division: { header: 'Division', getValue: (_item, c) => c.division || 'N/A' },
  assessor: { header: 'Assessor', getValue: (item) => item.assessor?.name || 'Unassigned' },
  contract_managers: {
    header: 'Contract Manager(s)',
    getValue: (_item, c) => c.contract_managers?.filter(Boolean).map((m) => m.name).join(', ') || 'N/A'
  },
  contract_owners: {
    header: 'Contract Owner(s)',
    getValue: (_item, c) => c.contract_owners?.filter(Boolean).map((o) => o.name).join(', ') || 'N/A'
  }
};

// Maps a Scheduler-table column key to the CSV column builder for that field.
// Column keys here match `contractColumns` in SchedulerDashboardPage.tsx (bug #1071) —
// a different key vocabulary from Assessment Meetings (e.g. singular
// contract_manager/contract_owner, plus urgency/in_progress), hence a separate map.
const SCHEDULER_EXPORT_COLUMNS: Record<string, ScheduleItemColumn> = {
  state: { header: 'Status', getValue: (_item, c) => c.state || 'N/A' },
  urgency: { header: 'Urgency', getValue: (_item, c) => c.urgency || 'N/A' },
  contract_number: { header: 'Contract Number', getValue: (_item, c) => c.contract_number },
  supplier: { header: 'Supplier', getValue: (_item, c) => c.supplier_name },
  tier: { header: 'Tier', getValue: (_item, c) => c.tier },
  assessor: { header: 'Assessor', getValue: (item) => item.assessor?.name || 'Unassigned' },
  contract_manager: {
    header: 'Contract Manager(s)',
    getValue: (_item, c) => c.contract_managers?.filter(Boolean).map((m) => m.name).join(', ') || 'N/A'
  },
  contract_owner: {
    header: 'Contract Owner(s)',
    getValue: (_item, c) => c.contract_owners?.filter(Boolean).map((o) => o.name).join(', ') || 'N/A'
  },
  meeting_date: { header: 'Meeting Date', getValue: (item) => formatDateOnly(item.meeting?.scheduled_datetime, 'Not scheduled') },
  division: { header: 'Division', getValue: (_item, c) => c.division || 'N/A' },
  target_date: { header: 'Target Assessment Date', getValue: (_item, c) => formatCalendarDate(c.target_date) },
  in_progress: { header: 'Scheduled By', getValue: (item) => item.processed_by?.name || 'N/A' }
};

const SCHEDULE_ITEM_EXPORT_COLUMN_MAPS = {
  'assessor-meetings': ASSESSOR_MEETING_EXPORT_COLUMNS,
  scheduler: SCHEDULER_EXPORT_COLUMNS
} as const;

// `visibleColumnKeys` must match the calling page's currently visible column keys so
// the export mirrors what the user sees on screen (bug #1071). `page` selects which
// column vocabulary/map to use, since Assessment Meetings and Scheduler name their
// columns differently despite sharing this same flatten-to-CSV-rows shape.
export const exportScheduleItems = (
  scheduleItems: ScheduleItemExport[],
  page: keyof typeof SCHEDULE_ITEM_EXPORT_COLUMN_MAPS,
  visibleColumnKeys: string[],
  filename = 'meetings'
) => {
  const columnMap = SCHEDULE_ITEM_EXPORT_COLUMN_MAPS[page];
  const columns = visibleColumnKeys
    .map((key) => columnMap[key])
    .filter((column): column is ScheduleItemColumn => Boolean(column));

  // Flatten schedule items - one row per contract
  const exportData = scheduleItems.flatMap((item) =>
    item.contracts.map((contract) =>
      columns.reduce<Record<string, unknown>>((row, column) => {
        row[column.header] = column.getValue(item, contract);
        return row;
      }, {})
    )
  );

  downloadCSV(exportData, filename);
};
